import { AddCircleOutline } from "@mui/icons-material";
import { Alert, Box, DialogContent, Divider, FormHelperText, Skeleton, Stack, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { EnergyTransactionsSelectFilter } from "components/contracts/tariffs/EnergyTransactionsSelect";
import ConfigureCommunityMemberDialog from "components/dialogs/communities/ConfigureCommunityMemberDialog";
import CommunitySelectSlider from "components/forms/communities/CommunitySelectSlider";
import IndieBackButton from "components/forms/IndieBackButton";
import IndieButton from "components/forms/IndieButton";
import IndieContinueButton from "components/forms/IndieContinueButton";
import IndieDesktopDatePicker from "components/forms/IndieDesktopDatePickerV2";
import IndieSaveButton from "components/forms/IndieSaveButton";
import IndieOutlinedCardSection from "components/IndieOutlinedCardSection";
import { DRF_DATE_FORMAT } from "constants";
import { AgentContextProvider, useAgentContext } from "contexts/AgentContext";
import { CommunityContextProvider, useCommunityContext } from "contexts/communities/CommunityContext";
import { TariffsContextProvider } from "contexts/tariffs/TariffsContext";
import { useUserFeedbackContext } from "contexts/UserFeedbackContext";
import { Form, Formik, useFormikContext } from "formik";
import _ from "lodash";
import moment from "moment";
import ApplicationList from "page-sections/common/ApplicationList";
import { EnergyFeeFilter, PlanFilter, PlanTypeFilter, PowerFeeFilter, ProductFilter } from "pages/core/tariffs/tariff-models-list";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTariffsAPIService } from "service/core/TariffsAPIService";
import * as Yup from "yup";
import { IndieLabelAndValue } from "../communities/NewCommunityDialog";

const NewCommunityTariffPlanDialogContent = ({ open, handleSuccess, initialCommunity = null, initialInstallation = null, initialMember = null }) => {

    const { setAndShowErrorMessage, setAndShowSuccessMessage } = useUserFeedbackContext();
    const tariffsAPI = useTariffsAPIService();

    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const [selectedCommunity, setSelectedCommunity] = useState(
        initialMember ? initialMember.physical_unit?.community : (initialInstallation ? initialInstallation.community : initialCommunity)
    );

    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    const initialValues = {
        selected_community: initialMember ? initialMember.physical_unit?.community : (initialInstallation ? initialInstallation.community : initialCommunity),
        selected_tariff_model: null,
        selected_members: [],
        signature_date: moment(),
        start_date: moment(),
        auto_renew: true,
    };

    const step1ValidationSchema = Yup.object().shape({
        selected_community: Yup.object().required(t("error.forms.mandatory")),
    });

    const step2ValidationSchema = Yup.object().shape({
        selected_tariff_model: Yup.object().required(t("error.forms.mandatory")),
    });

    // Role (consumer/producer) is assigned by the backend when the plan is created, so the only
    // client-side rule left here is that at least one member must be selected — pair-type plans
    // (exactly 1 consumer + 1 producer) are validated server-side, where the role is known.
    const step3ValidationSchema = Yup.object().shape({
        selected_members: Yup.array().min(1, t("error.forms.newCommunityTariffPlan.atLeastOneMember")).required(t("error.forms.newCommunityTariffPlan.membersRequired")),
    });

    const step4ValidationSchema = Yup.object().shape({
        signature_date: Yup.date().required(t("error.forms.mandatory")),
        start_date: Yup.date().required(t("error.forms.mandatory")),
    });

    const step5ValidationSchema = Yup.object().shape({
    });

    const validationSchemas = [step1ValidationSchema, step2ValidationSchema, step3ValidationSchema, step4ValidationSchema, step5ValidationSchema];

    const handleSubmit = (values) => {
        if (activeStep < 4) {
            handleNext();
        } else {
            const _values = _.cloneDeep(values);
            _values['signature_date'] = moment(_values['signature_date']).format(DRF_DATE_FORMAT);
            _values['start_date'] = moment(_values['start_date']).format(DRF_DATE_FORMAT);

            console.log(_values);
            setSubmitting(true);

            tariffsAPI.createCommunityTariffPlan(_values, (response) => {
                setAndShowSuccessMessage(t("success.api.create"));
                setSubmitting(false);
                setActiveStep(0);
                if (handleSuccess) {
                    handleSuccess(response.data);
                }
            }, (error) => {
                setSubmitting(false);
                setAndShowErrorMessage(error);
            });
        }
    };

    const handleCommunityRowClick = (row, formik) => {
        setSelectedCommunity(row);
        formik.setFieldValue("selected_community", row);
    };

    useEffect(() => {
        setActiveStep(0);
    }, [open]);

    return (
        <DialogContent>
            <Divider fullWidth sx={{ mb: 2 }} />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchemas[activeStep]}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <Form>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            <Step key={0}>
                                <StepLabel>{t("label.step.newCommunityTariffPlan.communityCodeStep")}</StepLabel>
                                <StepContent>
                                    <Box maxWidth={"lg"}>
                                        <Stack spacing={2}>
                                            {initialCommunity || initialInstallation || initialMember ? (
                                                <Alert width={"md"} severity="success" variant="outlined">{`${t("newTarriffPlan.initialCommunityData")} ${initialCommunity?.name || initialInstallation?.community?.name || initialMember?.physical_unit?.community?.name}`}</Alert>
                                            ) : (
                                                <CommunitySelectSlider name="selected_community" onChange={(c) => handleCommunityRowClick(c, formik)} />
                                            )}
                                            {formik.errors.selected_community && <FormHelperText id="selected_community_helper_text" error>{formik.errors.selected_community}</FormHelperText>}
                                            <Stack direction="row" spacing={2}>
                                                <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </StepContent>
                            </Step>
                            <Step key={1}>
                                <StepLabel>{t("label.step.newCommunityTariffPlan.tariffModelStep")}</StepLabel>
                                <StepContent>
                                    <TariffsContextProvider>
                                        <AgentContextProvider id={selectedCommunity?.agent.id}>
                                            <CommunityContextProvider community={selectedCommunity}>
                                                <SelectTariffModelStep handleBack={handleBack} initialInstallation={initialInstallation} initialMember={initialMember} />
                                            </CommunityContextProvider>
                                        </AgentContextProvider>
                                    </TariffsContextProvider>
                                </StepContent>
                            </Step>
                            <Step key={2}>
                                <StepLabel>{t("label.step.newCommunityTariffPlan.communityMembersStep")}</StepLabel>
                                    <StepContent>
                                        <CommunityContextProvider community={selectedCommunity}>
                                            <SelectConsumersProducersStep handleBack={handleBack} initialInstallation={initialInstallation} initialMember={initialMember} />
                                        </CommunityContextProvider>
                                </StepContent>
                            </Step>
                            <Step key={3}>
                                <StepLabel>{t("label.step.newCommunityTariffPlan.activationDateStep")}</StepLabel>
                                <StepContent>
                                    <Stack spacing={2}>
                                        <IndieDesktopDatePicker id="signature_date" name="signature_date" label={t("label.form.signatureDate")} sx={{ width: 200 }} />
                                        <IndieDesktopDatePicker id="start_date" name="start_date" label={t("label.form.startDate")} sx={{ width: 200 }} />
                                        <Stack direction="row" spacing={2}>
                                            <IndieBackButton variant="outlined" onClick={handleBack} />
                                            <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                                        </Stack>
                                    </Stack>
                                </StepContent>
                            </Step>
                            <Step key={4}>
                                <StepLabel>{t("label.step.newCommunityTariffPlan.confirmationStep")}</StepLabel>
                                <StepContent>
                                    <Stack spacing={4}>
                                        <NewCommunityTariffPlanConfirmationStep />
                                        <Stack direction="row" spacing={2}>
                                            <IndieBackButton variant="outlined" onClick={handleBack} disabled={submitting} />
                                            <IndieSaveButton variant="contained" id="save_button" type="submit" loading={submitting} disabled={submitting} />
                                        </Stack>
                                    </Stack>
                                </StepContent>
                            </Step>
                        </Stepper>
                        {/**<pre>{JSON.stringify(formik.errors)}</pre>*/}
                        {/**<pre>{JSON.stringify(formik.values)}</pre>*/}
                    </Form>
                )}
            </Formik>
        </DialogContent>
    );
};

const NewCommunityTariffPlanConfirmationStep = () => {

    const { values } = useFormikContext();
    const { t } = useTranslation();

    return (
        <Fragment>
            <Stack spacing={2}>
                <Typography variant='subtitle2' color='textSecondary'>
                    {t("label.step.newCommunityTariffPlan.confirmationStep.subtitle")}
                </Typography>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunityTariffPlan.communityCodeStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.selected_community?.name} />
                    <IndieLabelAndValue label="label.form.totalMembers" value={values.selected_community?.total_members} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunityTariffPlan.tariffModelStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label="label.form.name" value={values.selected_tariff_model?.name} />
                    <IndieLabelAndValue label="label.form.planType" value={t(`label.tariffPlanType.${values.selected_tariff_model?.plan_type.code}`)} />
                    <IndieLabelAndValue label="label.form.product" value={t(`label.product.${values.selected_tariff_model?.product.code}`)} />
                    <IndieLabelAndValue label="label.form.plan" value={values.selected_tariff_model?.plan.description} />
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunityTariffPlan.communityMembersStep")}</Typography>
                    <Divider />
                    {values.selected_members.length > 0 && (
                        <Stack spacing={1}>
                            <Typography variant='subtitle2' color='textSecondary'> {t("label.form.members")} </Typography>
                            {values.selected_members.map((member, index) => (
                                <Fragment key={member.id}>
                                    <IndieLabelAndValue label={t("label.form.name")} value={member.member.name} />
                                    <IndieLabelAndValue label={t("label.form.fiscalNumber")} value={member.member.fiscal_number} />
                                    <IndieLabelAndValue label={t("label.form.consumptionDeliveryPoint")} value={member.physical_unit?.consumption_deliverypoint} />
                                    <IndieLabelAndValue label={t("label.form.productionDeliveryPoint")} value={member.physical_unit?.production_deliverypoint} />
                                    {index < values.selected_members.length - 1 && (
                                        <Box sx={{ width: '18%', margin: '0 auto' }}>
                                            <Divider />
                                        </Box>
                                    )}
                                </Fragment>
                            ))}
                        </Stack>
                    )}
                </Stack>
                <Stack spacing={1}>
                    <Typography variant='subtitle2' color='textSecondary'>{t("label.step.newCommunityTariffPlan.activationDateStep")}</Typography>
                    <Divider />
                    <IndieLabelAndValue label={t("label.form.signatureDate")} value={values.signature_date ? values.signature_date.format("DD/MM/YYYY") : ""} />
                    <IndieLabelAndValue label={t("label.form.startDate")} value={values.start_date ? values.start_date.format("DD/MM/YYYY") : ""} />
                </Stack>
            </Stack>
        </Fragment>
    )
}

const SelectTariffModelStep = ({ handleBack, initialInstallation = null, initialMember = null }) => {

    const { t } = useTranslation();
    const { agent, loadingAgent, getCommunitiesTariffModels } = useAgentContext();
    const { submitting: loadingCommunity, community } = useCommunityContext();

    const formik = useFormikContext();

    const columns = useMemo(() => [
        { accessor: "name", Header: t("label.table.header.name"), },
        {
            accessor: "plan_type.id",
            Header: t("label.table.header.planType"),
            Cell: ({ row }) => t(`label.tariffPlanType.${row.original.plan_type.code}`),
            filterComponent: ({ column, fetchData, state }) => <PlanTypeFilter column={column} fetchData={fetchData} state={state} />,
        },
        {
            accessor: "product.id",
            Header: t("label.table.header.transaction"),
            Cell: ({ row }) => t(`label.product.${row.original.product.code}`),
            filterComponent: ({ column, fetchData, state }) => <ProductFilter column={column} fetchData={fetchData} state={state} />,
        },
        {
            accessor: "energy_transaction.id",
            Header: t("label.table.header.energy"),
            Cell: ({ row }) => t(`label.energyTransaction.${row.original.energy_transaction.code}`),
            filterComponent: ({ column, fetchData, state }) => <EnergyTransactionsSelectFilter column={column} fetchData={fetchData} state={state} />,
        },
        {
            accessor: "plan.id",
            Header: t("label.table.header.energyTariff"),
            Cell: ({ row }) => {
                return <Typography variant="body2">{row.original.plan.description}</Typography>
            },
            filterComponent: ({ column, fetchData, state }) => <PlanFilter column={column} fetchData={fetchData} state={state} />,
        },
        {
            accessor: "power_fee.id",
            Header: t("label.table.header.fixedTerm"),
            Cell: ({ row }) => {
                return <Typography variant="body2">{row.original.power_fee.description}</Typography>
            },
            filterComponent: ({ column, fetchData, state }) => <PowerFeeFilter column={column} fetchData={fetchData} state={state} />,
        },
        {
            accessor: "energy_fee.id",
            Header: t("label.table.header.variableTerm"),
            Cell: ({ row }) => {
                return <Typography variant="body2">{row.original.energy_fee.description}</Typography>
            },
            filterComponent: ({ column, fetchData, state }) => <EnergyFeeFilter column={column} fetchData={fetchData} state={state} />,
        },
    ], [t]);

    // Used to auto-filter this list down to just the pre-selected installation's/member's
    // producer-or-consumer role, regardless of entry point — but that role isn't decided here,
    // it's resolved by the backend only once the plan is actually created (see
    // CommunityMembers.create_tariff_plan), so every entry point must show every model.
    const getCommunitiesTariffModelsWrapper = (filters=[], page_size, page, sortColumns, callBack, errorCallBack) => {
        const communityFilter = { id: "community", value: community.id};
        filters.push(communityFilter);
        getCommunitiesTariffModels(filters, page_size, page, sortColumns, callBack, errorCallBack)
    }


    const onRowChange = (row) => {
        formik.setFieldValue("selected_tariff_model", row);
    };

    if(loadingCommunity || loadingAgent) {
        return <Skeleton variant="rectangular" width="100%" height={400} />
    }

    if(!agent || !community) {
        return <Alert severity="error">{t("error.forms.newCommunityTariffPlan.noAgentOrCommunity")}</Alert>
    }

    return (
        <Box maxWidth={"xl"}>
            <Stack spacing={2}>
                <ApplicationList
                    id="tariff_models_list"
                    dataFetcher={getCommunitiesTariffModelsWrapper}
                    columns={columns}
                    rowOnClick2={onRowChange}
                    selectable
                    selected={formik.values.selected_tariff_model}
                    outlined
                    showAppBar
                />

                {formik.errors.selected_tariff_model && <FormHelperText id="selected_tariff_model_helper_text" error>{formik.errors.selected_tariff_model}</FormHelperText>}
                <Stack direction="row" spacing={2}>
                    <IndieBackButton variant="outlined" onClick={handleBack} />
                    <IndieContinueButton id="continue_button" variant="contained" type="submit" />
                </Stack>
            </Stack>
        </Box>
    )
}

const SelectConsumersProducersStep = ({ handleBack, initialInstallation = null, initialMember = null }) => {

    const { t } = useTranslation();
    const formik = useFormikContext();
    const { submitting: loadingCommunity, community, getAllCommunityMembers } = useCommunityContext();

    const [openCreateMemberDialog, setOpenCreateMemberDialog] = useState(false);
    const tableRef = useRef();

    // Filtering by consumption_deliverypoint missed pure-producer installations entirely (they
    // have no consumption CPE) — community_installation is the direct, always-present link
    // between this installation and its CommunityMembers rows, regardless of role.
    const communityInstallationId = initialInstallation?.community_installation?.id;
    const installationFilters = communityInstallationId ? [{ id: "community_installation", value: communityInstallationId }] : [];

    const membersColumns = useMemo(() => [
        { accessor: 'member.name', Header: t("label.table.header.name"), },
        { accessor: 'member.fiscal_number', Header: t("label.table.header.fiscalNumber"), filter: "contains" },
        { accessor: 'physical_unit.consumption_deliverypoint', Header: t("label.table.header.consumptionDeliverypoint"), filter: "contains" },
        { accessor: 'physical_unit.production_deliverypoint', Header: t("label.table.header.productionDeliverypoint") },
        {
            accessor: 'active',
            Header: t("label.table.header.status"),
            Cell: ({ value }) => t(value ? "label.status.member.active" : "label.status.member.inactive"),
        },
    ], [t]);

    // Role (consumer/producer) is assigned by the backend when the plan is created — the frontend
    // just forwards the flat list of selected CommunityMembers.
    const handleSelectionChange = (selectedMembers) => {
        formik.setFieldValue("selected_members", selectedMembers);
    };

    // Deliberately does not auto-select the newly-created member — this step decides who gets
    // billed, so the user picks it explicitly from the refreshed list instead of it happening
    // implicitly on their behalf.
    const handleCreateMemberSuccess = () => {
        setOpenCreateMemberDialog(false);
        tableRef.current?.refreshData();
    };

    // When arriving from "Configurar Cliente", the member is already known — pre-select it
    // automatically (same treatment as the community step's Alert when pre-filled) instead of
    // making the user pick it again from the list.
    useEffect(() => {
        if (!initialMember?.physical_unit) return;

        const memberRow = {
            id: initialMember.id,
            member: initialMember.client || initialMember.member,
            physical_unit: initialMember.physical_unit,
        };

        handleSelectionChange([memberRow]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialMember]);

    if(loadingCommunity) {
        return <Skeleton variant="rectangular" width="100%" height={400} />
    }

    if(!community) {
        return <Alert severity="error">{t("error.forms.newCommunityTariffPlan.noCommunity")}</Alert>
    }

    return (
        <Stack spacing={2}>
            <Box maxWidth={"lg"}>
                {initialMember ? (
                    <Alert width={"md"} severity="success" variant="outlined">
                        <Stack spacing={0.5}>
                            <Typography variant="body2">{t("newTarriffPlan.initialMemberData")}</Typography>
                            <IndieLabelAndValue label={t("label.form.name")} value={initialMember.client?.name || initialMember.member?.name} />
                            <IndieLabelAndValue label={t("label.form.fiscalNumber")} value={initialMember.client?.fiscal_number || initialMember.member?.fiscal_number} />
                            <IndieLabelAndValue label={t("label.form.consumptionDeliveryPoint")} value={initialMember.physical_unit?.consumption_deliverypoint} />
                            <IndieLabelAndValue label={t("label.form.productionDeliveryPoint")} value={initialMember.physical_unit?.production_deliverypoint} />
                        </Stack>
                    </Alert>
                ) : (
                    <IndieOutlinedCardSection title={t("label.step.newCommunityTariffPlan.communityMembersStep")}>
                        <ApplicationList
                            ref={tableRef}
                            id="community_members_list"
                            dataFetcher={getAllCommunityMembers}
                            columns={membersColumns}
                            rowOnClick2={handleSelectionChange}
                            additionalFilters={installationFilters}
                            selectable
                            multiple
                            selected={formik.values.selected_members}
                            outlined
                            showAppBar
                            appBar={[
                                <IndieButton key="create_member" id="button_create_member" startIcon={<AddCircleOutline />} variant="outlined" onClick={() => setOpenCreateMemberDialog(true)}>
                                    {t("label.button.createMember")}
                                </IndieButton>
                            ]}
                        />
                    </IndieOutlinedCardSection>
                )}
                {formik.errors.selected_members && (
                    <FormHelperText id="selected_members_helper_text" error>{formik.errors.selected_members}</FormHelperText>
                )}
                <ConfigureCommunityMemberDialog
                    open={openCreateMemberDialog}
                    onClose={() => setOpenCreateMemberDialog(false)}
                    onSuccess={handleCreateMemberSuccess}
                    fullScreen={false}
                />
            </Box>
            <Stack direction="row" spacing={2}>
                <IndieBackButton variant="outlined" onClick={handleBack} />
                <IndieContinueButton id="continue_button" variant="contained" type="submit" />
            </Stack>
        </Stack>
    )

}
export default NewCommunityTariffPlanDialogContent;